import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { reloadToLatest } from "../lib/update";

/** How far to pull (px) before letting go refreshes. */
const TRIGGER = 72;

/** Opened from the home screen: the browser's own pull-to-refresh is missing there, so the app brings its own. */
const standalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * Pull down at the top of any page and let go: the app reloads with the newest version, like every phone app.
 * Not while typing, in a sheet or on the map, where a downward drag means something else.
 */
export function PullToRefresh() {
  const { t } = useI18n();
  const [pull, setPull] = useState(0);
  const start = useRef<number | null>(null);

  useEffect(() => {
    if (!standalone()) return;
    const onStart = (e: TouchEvent) => {
      const el = e.target as Element | null;
      if (window.scrollY > 0 || e.touches.length !== 1 || el?.closest("input, textarea, select, [role=dialog], .sheet, .leaflet-container")) {
        start.current = null;
        return;
      }
      start.current = e.touches[0]!.clientY;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const d = e.touches[0]!.clientY - start.current;
      // Resistance: the indicator follows at half speed and stops a little past the trigger.
      setPull(d > 0 && window.scrollY <= 0 ? Math.min(TRIGGER * 1.4, d / 2) : 0);
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      setPull((p) => {
        if (p >= TRIGGER) void reloadToLatest();
        return p >= TRIGGER ? p : 0;
      });
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  if (pull <= 4) return null;
  const ready = pull >= TRIGGER;
  return (
    <div className={`ptr ${ready ? "ready" : ""}`} style={{ transform: `translate(-50%, ${pull}px)` }} role="status" aria-label={t("common.refresh")}>
      <RefreshCw size={20} style={{ transform: `rotate(${pull * 4}deg)` }} />
    </div>
  );
}
